import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import {
  DeleteMenuItemWithConfirmationModal,
  DeleteMenuItemWithConfirmationModalProps,
} from '@/components/poppers/MenuPopper/DeleteMenuItemWithConfirmationModal';
import { useReportError } from '@/hooks/useReportError';
import {
  BehaviorAuthorizationType,
  useHasBehaviorAuthorization,
} from '@/modules/authentication/hooks/useHasBehaviorAuthorization';

import { useDeleteTestamentaryEntityMutation } from '../testamentaryEntities/graphql/DeleteTestamentaryEntity.generated';

interface DeleteTestamentaryEntityMenuItemProps
  extends Partial<DeleteMenuItemWithConfirmationModalProps> {
  testamentaryEntityId: string;
  onDelete: () => void;
  modalProps?: Record<string, unknown>;
  menuItemProps?: Record<string, unknown>;
}

export function DeleteTestamentaryEntityMenuItem({
  testamentaryEntityId,
  onDelete,
  modalProps = {},
  menuItemProps = {},
  ...deleteProps
}: DeleteTestamentaryEntityMenuItemProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const canDelete = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_DELETE_ENTITIES_AND_PROPOSALS
  );

  const [deleteTestamentaryEntity, { loading: deleting }] =
    useDeleteTestamentaryEntityMutation({
      variables: { id: testamentaryEntityId },
      onError: (error) => {
        reportError(`failed to delete sub-trust`, error);
        showFeedback(
          'Failed to delete this sub-trust. Please refresh the page and try again.'
        );
      },
      onCompleted: () => {
        showFeedback('Sub-trust deleted successfully', { variant: 'success' });
        onDelete();
      },
    });

  // Don't render the menu item if the user doesn't have delete permissions
  if (!canDelete) {
    return null;
  }

  return (
    <DeleteMenuItemWithConfirmationModal
      label="Delete sub-trust"
      onConfirmDelete={() => deleteTestamentaryEntity()}
      {...deleteProps}
      menuItemProps={{ loading: deleting, ...menuItemProps }}
      modalProps={{
        heading: 'Are you sure you want to delete this sub-trust?',
        ...modalProps,
      }}
    />
  );
}
